import { Magnetize } from './magnetize';

export const DEFAULT_SPACING = 20;
export const DEFAULT_SPACING_BOTTOM = 30;

export class MagnetizeElise extends Magnetize {
  eliseElement: HTMLElement | null;
  private options?: {
    absoluteBottom?: number;
    horizontalPadding?: number;
  };

  constructor(
    widgetElement: HTMLElement,
    offset: {
      bottom: number;
      left?: number;
      right?: number;
    },
    options: {
      absoluteBottom?: number;
      horizontalPadding?: number;
    } = {}
  ) {
    const targetElement = MagnetizeElise.getEliseElement();

    super(targetElement, widgetElement, offset);
    this.options = options;

    this.eliseElement = targetElement;
  }

  public init() {
    const targetElement = this.targetElement;

    // Initialize ResizeObserver to update position on size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.updatePosition(targetElement);
    });
    this.resizeObserver.observe(targetElement);
    this.resizeObserver.observe(this.widgetElement);

    // Debounce scroll and resize events (~60fps)
    this.scrollHandler = this.debounce(() => {
      this.updatePosition(targetElement);
    }, 16);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.scrollHandler, { passive: true });

    // Set initial position
    this.updatePosition(targetElement);
  }

  updatePosition(observedElement: HTMLElement) {
    const targetElement = this.getOpenEliseElement() ?? observedElement;
    if (
      !this.isInDocument(targetElement) ||
      targetElement.style.display === 'none'
    ) {
      document.body.style.removeProperty('--rg-magnetize-bottom');
      this.widgetElement.style.position = 'fixed';
      this.widgetElement.style.bottom = `${this.offset.bottom}px`;
      if (this.offset.left !== undefined) {
        this.widgetElement.style.left = `${this.offset.left}px`;
        this.widgetElement.style.right = '';
      } else if (this.offset.right !== undefined) {
        this.widgetElement.style.right = `${this.offset.right}px`;
        this.widgetElement.style.left = '';
      }
      this.cleanup();
      return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    const widgetRect = this.widgetElement.getBoundingClientRect();

    let bottom = window.innerHeight - targetRect.top + this.offset.bottom;

    if (bottom > window.innerHeight || bottom < 0) {
      bottom = DEFAULT_SPACING;
    }

    // Set the CSS variable for use in other components
    document.body.style.setProperty('--rg-magnetize-bottom', `${bottom}px`);

    let left: number | null = null;
    if (this.offset.left !== undefined) {
      left = targetRect.left + this.offset.left;
      if (left < 0) {
        left = 0;
      } else if (left + widgetRect.width > window.innerWidth) {
        left = window.innerWidth - widgetRect.width;
      }
    } else if (this.offset.right !== undefined) {
      left = targetRect.right - widgetRect.width - this.offset.right;
      if (left < 0) {
        left = 0;
      } else if (left + widgetRect.width > window.innerWidth) {
        left = window.innerWidth - widgetRect.width;
      }
    }

    this.widgetElement.style.position = 'fixed';
    this.widgetElement.style.bottom = `var(--rg-magnetize-bottom)`;

    if (typeof this.options.absoluteBottom === 'number') {
      this.widgetElement.style.bottom = `${this.options.absoluteBottom}px`;
    }

    if (left !== null) {
      if (typeof this.options.horizontalPadding === 'number' && left > 0) {
        this.widgetElement.style.left = `${left - this.options.horizontalPadding}px`;
      } else if (left === 0) {
        this.widgetElement.style.left = `${DEFAULT_SPACING}px`;
      } else {
        this.widgetElement.style.left = `${left}px`;
      }
      this.widgetElement.style.right = '';
    }
  }

  getOpenEliseElement(): HTMLElement | null {
    return (
      this.eliseElement?.querySelector('email-us-window#email-us-window') ||
      this.eliseElement?.querySelector('call-us-window#call-us-window')
    );
  }

  static getEliseElement(): HTMLElement | null {
    const meetEliseChatList = document.querySelectorAll(
      'me-chat.meetelise-chat'
    );

    let eliseElement: HTMLElement | null = null;

    for (const chatRoot of Array.from(meetEliseChatList)) {
      const shadowRoot = chatRoot?.shadowRoot;
      const launcherRoot =
        shadowRoot?.getElementById('meetelise-launcher')?.shadowRoot;

      if (launcherRoot) {
        eliseElement =
          launcherRoot.querySelector('div.launcher__mini-launcher-wrapper') ??
          launcherRoot.querySelector('div.launcher__mobile-launcher-wrapper') ??
          launcherRoot.querySelector('div');
      }

      if (eliseElement) {
        break;
      }
    }

    return eliseElement;
  }
}
